import { Formik, FormikHelpers } from "formik";
import React, { useCallback, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { ErrorsListCard } from "../../components/forms/errors-list";
import { TextFieldFormGroup } from "../../components/forms/groups/text-field-group";
import { SaveData } from "../../components/forms/save-data";
import { logError } from "../../errors";
import { SubmitState } from "../../util/loading";
import { getObject } from "../../util/local-storage";
import { FormState, StorageModel } from "../../components/forms/form-state";
import { apiConfiguration, withCsrf } from "../../api-configuration";
import styled from "styled-components";
import { ContactPUnitForm, ContactApi } from "../../lib-api";
import { object, Schema, string } from "yup";
import { TextAreaFormGroup } from "../../components/forms/groups/text-area-group";
import clientConfiguration from "../../client-configuration";

type Form = ContactPUnitForm;

const validationSchema: Schema<Form> = object().shape({
  email: string().label("Email").email().required(),
  name: string().label("Navn").required(),
  phone: string().label("Telefon").required(),
  message: string().label("Besked").required(),
});

function getInitialValues(savedValues?: Form): Form {
  return {
    name: savedValues?.name ?? "",
    email: savedValues?.email ?? "",
    phone: savedValues?.phone ?? "",
    message: "",
  };
}

function getInitialState(savedState?: FormState): FormState {
  return {
    errors: savedState?.errors ?? undefined,
    state: SubmitState.None,
  };
}

const api = new ContactApi(apiConfiguration);

const Container = styled("div")({
  backgroundColor: "#F4F7F7",
  fontFamily: "sans-serif",
});

export const ContactForm: React.FC<{
  pUnitId: string;
  pUnitName: string;
}> = ({ pUnitId, pUnitName }) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formState, setFormState] = useState<FormState>(getInitialState());

  const handleSubmit = useCallback(
    async (values: Form, helpers: FormikHelpers<Form>) => {
      try {
        setFormState({
          state: SubmitState.None,
        });

        await withCsrf(api, clientConfiguration.csrf).contactPUnit({
          id: pUnitId,
          contactPUnitForm: values,
        });

        setFormState({
          state: SubmitState.Submitted,
        });
      } catch (e) {
        if (e instanceof Response && e.status == 400) {
          const json = await e.json();

          e = new Error(JSON.stringify(json));

          setFormState({
            state: SubmitState.Error,
            errors: json.errors,
          });
        } else {
          setFormState({
            state: SubmitState.Error,
          });
        }

        if (!(e instanceof Error)) {
          e = new Error(JSON.stringify(e));
        }
        logError(e);
      }
    },
    [setFormState]
  );

  return (
    <Formik<Form>
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({
        handleChange,
        values,
        touched,
        errors,
        isSubmitting,
        handleSubmit,
      }) => (
        <Container className="p-4 rounded-corners">
          {formState.state === SubmitState.Submitted ? (
            <>
              <h2>
                Tak for din henvendelse, vi sørger for at sende den videre til{" "}
                {pUnitName}
              </h2>
            </>
          ) : (
            <>
              <Form onSubmit={handleSubmit}>
                <h2>Kontakt {pUnitName}</h2>
                <Row>
                  <TextFieldFormGroup
                    fieldName="name"
                    className="col-12"
                    fieldDisplayName="Navn *"
                    placeholder="Dit navn"
                    error={touched.name ? errors.name : undefined}
                    value={values.name}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <TextFieldFormGroup
                    fieldName="phone"
                    className="col-12"
                    fieldDisplayName="Telefonnummer *"
                    placeholder="Dit telefonnummer"
                    error={touched.phone ? errors.phone : undefined}
                    value={values.phone}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <TextFieldFormGroup
                    fieldName="email"
                    className="col-12"
                    fieldDisplayName="Email *"
                    placeholder="Din email"
                    error={touched.email ? errors.email : undefined}
                    value={values.email}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <TextAreaFormGroup
                    fieldName="message"
                    className="col-12"
                    fieldDisplayName="Besked *"
                    placeholder="Skriv din besked"
                    error={touched.message ? errors.message : undefined}
                    value={values.message}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <Form.Group className="col-12">
                    <Button
                      className="align-self-end mt-3"
                      disabled={isSubmitting}
                      variant="primary"
                      block={false}
                      type="submit"
                      size="lg"
                      onClick={() => setSubmitAttempted(true)}
                    >
                      Send besked
                    </Button>
                  </Form.Group>
                </Row>
              </Form>
              {submitAttempted && <ErrorsListCard errors={errors} />}
            </>
          )}
          {formState.state == SubmitState.Error && (
            <div>
              {formState.errors ? (
                <ErrorsListCard
                  title="Vores server meldte følgende fejl"
                  errors={formState.errors}
                />
              ) : (
                <>Ups, der opstod en fejl. Prøv evt. at genindlæse siden.</>
              )}
            </div>
          )}
        </Container>
      )}
    </Formik>
  );
};
